import { UiTagVariant } from '@infuse/ui/domain';

// --- Station Selectors ---

export interface StationSelectorById {
  type: 'ID';
  chargingStationIds: string[];
}

// Ids only: the backend has no filter-based selector, so "select all matching" pages the search for ids.
export type StationSelector = StationSelectorById;

// --- Batch Job Types ---

export enum BatchJobType {
  CONFIGURATION = 'CONFIGURATION',
  FIRMWARE = 'FIRMWARE'
}

// --- Create Request ---

export interface FirmwareBatchJobCreateRequest {
  stationSelector: StationSelector;
  startAt?: string;
  retrieveAt: string;
  maxRetryAttempts: number;
  location?: string;
  firmwareId?: string;
  retryInterval?: string;
}

/** Alias for firmware batch create request (used by ChargingStationsPage) */
export type Batch = FirmwareBatchJobCreateRequest;

export interface ConfigurationItem {
  key: string;
  value: string;
}

export interface ConfigurationBatchJobCreateRequest {
  stationSelector: StationSelector;
  startAt?: string;
  maxRetryAttempts: number;
  retryInterval?: string;
  configurationItems: ConfigurationItem[];
  rebootAfterFinish?: boolean;
}

// --- Cancel ---

export interface CancelBatchCommand {
  reason?: string;
}

// --- Generic Batch Job Response ---

export interface BatchJobTaskSummary {
  total: number;
  countByStatus: Record<string, number>;
}

export interface BatchJobLastFailure {
  reason?: string;
  message?: string;
  timestamp?: string;
}

export interface BatchJob {
  id: string;
  type?: string;
  status?: BatchJobStatus;
  startAt?: string;
  startedAt?: string;
  finishedAt?: string;
  lastFailure?: BatchJobLastFailure;
  taskSummary?: BatchJobTaskSummary;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// --- Firmware Batch Job Response ---

export interface FirmwareBatchJob {
  id: string;
  type?: string;
  status?: FirmwareBatchJobStatus;
  startAt?: string;
  startedAt?: string;
  finishedAt?: string;
  lastFailure?: BatchJobLastFailure;
  taskSummary?: BatchJobTaskSummary;
  payload?: FirmwareBatchJobPayload;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface FirmwareBatchJobPayload {
  location?: string;
  firmwareId?: string;
  retrieveAt?: string;
  retryInterval?: string;
  maxRetryAttempts?: number;
}

// --- Configuration Batch Job Response ---

export interface ConfigurationBatchJob {
  id: string;
  type?: string;
  status?: BatchJobStatus;
  startAt?: string;
  startedAt?: string;
  finishedAt?: string;
  lastFailure?: BatchJobLastFailure;
  taskSummary?: BatchJobTaskSummary;
  payload?: ConfigurationBatchJobPayload;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ConfigurationBatchJobPayload {
  configurationItems?: ConfigurationItem[];
  rebootAfterFinish?: boolean;
  retryInterval?: string;
  maxRetryAttempts?: number;
}

// --- Batch Task Response ---

/** Generic task shape for non-firmware /batch-task routes. */
export interface BatchTask {
  id: string;
  batchJobId: string;
  status?: string;
  batchJobType?: string;
  externalId?: string;
  startedAt?: string;
  finishedAt?: string;
  successReason?: string;
  successDetails?: string;
  lastFailure?: BatchJobLastFailure;
}

export interface FirmwareStationTask {
  id: string;
  batchJobId: string;
  status: FirmwareStationTaskStatus;
  batchJobType?: string;
  externalId?: string;
  startedAt?: string;
  finishedAt?: string;
  successReason?: string;
  successDetails?: string;
  lastFailure?: BatchJobLastFailure;
  payload?: FirmwareTaskPayload;
}

export interface FirmwareTaskPayload {
  location?: string;
  firmwareId?: string;
  retrieveAt?: string;
  retryInterval?: string;
  maxRetryAttempts?: number;
  lastUpdateFirmwareResponseStatus?: UpdateFirmwareResponseStatus;
  lastUpdateFirmwareResponseStatusAt?: string;
  lastFirmwareStatus?: FirmwareStatus;
  lastFirmwareStatusAt?: string;
}

// --- Enums ---

export enum BatchJobStatus {
  CREATION_IN_PROGRESS = 'CREATION_IN_PROGRESS',
  CREATION_FAILED = 'CREATION_FAILED',
  CREATED = 'CREATED',
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
  TIMED_OUT = 'TIMED_OUT',
  SUCCESS = 'SUCCESS'
}

export type FirmwareBatchJobStatus = BatchJobStatus;

const FINISHED_BATCH_JOB_STATUSES: ReadonlySet<BatchJobStatus> = new Set([
  BatchJobStatus.CREATION_FAILED,
  BatchJobStatus.CANCELED,
  BatchJobStatus.FAILED,
  BatchJobStatus.TIMED_OUT,
  BatchJobStatus.SUCCESS
]);

export function isBatchJobFinished(status?: BatchJobStatus): boolean {
  return status != null && FINISHED_BATCH_JOB_STATUSES.has(status);
}

export enum FirmwareStationTaskStatus {
  READY = 'READY',
  ACTIVE = 'ACTIVE',
  WAITING_FOR_REBOOT = 'WAITING_FOR_REBOOT',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  TIMED_OUT = 'TIMED_OUT'
}

export enum UpdateFirmwareResponseStatus {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  ACCEPTED_CANCELED = 'ACCEPTED_CANCELED',
  INVALID_CERTIFICATE = 'INVALID_CERTIFICATE',
  REVOKED_CERTIFICATE = 'REVOKED_CERTIFICATE'
}

export enum FirmwareStatus {
  DOWNLOADING = 'DOWNLOADING',
  DOWNLOADED = 'DOWNLOADED',
  DOWNLOAD_FAILED = 'DOWNLOAD_FAILED',
  DOWNLOAD_SCHEDULED = 'DOWNLOAD_SCHEDULED',
  DOWNLOAD_PAUSED = 'DOWNLOAD_PAUSED',
  INSTALLING = 'INSTALLING',
  INSTALLED = 'INSTALLED',
  INSTALLATION_FAILED = 'INSTALLATION_FAILED',
  INSTALL_REBOOTING = 'INSTALL_REBOOTING',
  INSTALL_SCHEDULED = 'INSTALL_SCHEDULED',
  INSTALL_VERIFICATION_FAILED = 'INSTALL_VERIFICATION_FAILED',
  INVALID_SIGNATURE = 'INVALID_SIGNATURE',
  SIGNATURE_VERIFIED = 'SIGNATURE_VERIFIED',
  IDLE = 'IDLE'
}

export const cancelableBatchJobStatuses: BatchJobStatus[] = [
  BatchJobStatus.CREATION_IN_PROGRESS,
  BatchJobStatus.CREATED,
  BatchJobStatus.ACTIVE
];

export const cancelableBatchTaskStatuses: string[] = [
  FirmwareStationTaskStatus.READY,
  FirmwareStationTaskStatus.ACTIVE,
  FirmwareStationTaskStatus.WAITING_FOR_REBOOT
];

// --- Status Colour Mappings ---

export const FirmwareBatchJobStatusColour: Record<string, UiTagVariant> = {
  [BatchJobStatus.CREATION_IN_PROGRESS]: 'warning',
  [BatchJobStatus.CREATION_FAILED]: 'error',
  [BatchJobStatus.CREATED]: 'warning',
  [BatchJobStatus.ACTIVE]: 'primary',
  [BatchJobStatus.CANCELED]: 'error',
  [BatchJobStatus.FAILED]: 'error',
  [BatchJobStatus.TIMED_OUT]: 'error',
  [BatchJobStatus.SUCCESS]: 'success'
};

export const FirmwareStationTaskStatusColour: Record<string, UiTagVariant> = {
  [FirmwareStationTaskStatus.READY]: 'neutral',
  [FirmwareStationTaskStatus.ACTIVE]: 'primary',
  [FirmwareStationTaskStatus.WAITING_FOR_REBOOT]: 'warning',
  [FirmwareStationTaskStatus.SUCCESS]: 'success',
  [FirmwareStationTaskStatus.FAILED]: 'error',
  [FirmwareStationTaskStatus.CANCELED]: 'error',
  [FirmwareStationTaskStatus.TIMED_OUT]: 'error'
};

// --- Selection → Selector mapping ---

// Structural copy of `StationSelection` so this api lib needs no charging-station store dependency.
export type StationSelectionInput = { mode: 'ids'; rows: { id: string }[] };

// Filter-mode selections are resolved to ids by the caller (`fetchAllMatchingIds`) before they get here.
export function toStationSelector(selection: StationSelectionInput): StationSelector {
  return { type: 'ID', chargingStationIds: selection.rows.map((row) => row.id) };
}
